import * as XLSX from "xlsx";
import type { ImportedData, SheetColumnDef } from "./types";

/**
 * Read a File as text (for CSV) or as ArrayBuffer (for XLSX)
 */
function readFileContent(
  file: File,
  type: "csv" | "xlsx"
): Promise<string | ArrayBuffer> {
  if (type === "csv") {
    // Modern browsers:
    if (typeof file.text === "function") {
      return file.text();
    }

    // Fallback:
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsText(file);
    });
  }

  // XLSX: prefer arrayBuffer()
  if (typeof file.arrayBuffer === "function") {
    return file.arrayBuffer();
  }

  // Fallback to FileReader
  return new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Import CSV or XLSX and return sheets with headers + data
 */
export async function importData(
  file: File,
  type: "xlsx" | "csv"
): Promise<ImportedData> {
  const content = await readFileContent(file, type);

  if (type === "csv") {
    const text = content as string;
    const [headerLine, ...lines] = text.split(/\r?\n/).filter(Boolean);
    const headers = headerLine.split(",").map((h) => h.replace(/^"|"$/g, ""));
    const data = lines.map((line) => {
      const values = line.split(",").map((v) => v.replace(/^"|"$/g, ""));

      return headers.reduce((obj, h, i) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // eslint-disable-next-line no-param-reassign
        obj[h] = values[i];

        return obj;
      }, {});
    });

    return {
      fileName: file.name.replace(/\.[^.]+$/, ""),
      fileNameWithExtension: file.name,
      sheets: [
        {
          sheetName: file.name.replace(/\.[^.]+$/, ""),
          headers,
          data,
        },
      ],
    };
  }
  // XLSX path: ArrayBuffer → sheet
  const arrayBuffer = content as ArrayBuffer;
  const wb = XLSX.read(arrayBuffer, { type: "array", cellDates: true });
  const sheets = wb.SheetNames.map((name) => {
    const ws = wb.Sheets[name];
    const data = XLSX.utils.sheet_to_json(ws, { defval: "", raw: true });
    const headers = (XLSX.utils.sheet_to_json(ws, {
      header: 1,
      raw: true,
    })[0] || []) as string[];

    return { sheetName: name, headers, data };
  });

  return {
    fileName: file.name.replace(/\.[^.]+$/, ""),
    fileNameWithExtension: file.name,
    sheets,
  };
}

/**
 * Given raw row objects and your SheetColumnDef list, returns
 * a new array of objects where each key is the column.key
 * and each value is the corresponding field from the row.
 */
export function mapDataByColumns<T>(
  data: T[],
  columns: SheetColumnDef<T>[]
): Array<T> {
  return data?.map((row) => {
    const obj: Record<string, unknown> = {};

    for (const col of columns) {
      const header = col.header ?? String(col.key);

      obj[String(col.key)] = row[header as keyof T];
    }

    return obj as T;
  });
}
